import { SubscriptionLevel } from '@/api/model/profile';
import { ulid } from 'ulid';
import type { 
    SubscriptionProductRecord, 
    StripeCheckoutRequest, 
    StripeCheckoutResponse,
    StripeCheckoutSessionStatus,
    StripePortalSessionRequest,
    StripePortalSessionResponse
} from '@/api/model/stripe';
import { saveAppState } from '@/util/app-state';
import Authorizor from '@/util/authorizor';
import { UserNotificationManager } from '@/util/user-notification-manager';
import { apiAddress } from '@/util/http-utils';
import { formatCurrency } from '@/util/currency';
import type { UrlResponse } from '@/api/model/url-response';

const catalogSessionKey = "product_catalog";


export const getProductCatalog = async (): Promise<SubscriptionProductRecord | undefined> => {
    let catalog = loadPersistedProductCatalog();
    if (catalog) {
        return catalog;
    }
    const response = await fetch(apiAddress("/subscription/products"), {
        headers: Authorizor.headers()
    });
    if (response.ok) {
        const catalog = await response.json();
        persistProductCatalog(catalog);
        return catalog;
    }
}

export const createCheckoutSession = async (priceId: string, returnUrl: string): Promise<StripeCheckoutResponse | undefined> => {
    const args: StripeCheckoutRequest = {
        priceId: priceId,
        returnUrl: returnUrl,
    }
    const url = apiAddress("/subscription/checkout");
    const options = {
        method: "POST",
        headers: Authorizor.headers(),
        body: JSON.stringify(args)
    }
    const response = await fetch(url, options);
    if (response.ok) {
        return await response.json();
    }
}

export const getCheckoutStatus = async (sessionId: string): Promise<StripeCheckoutSessionStatus | undefined> => {
    const response = await fetch(apiAddress(`/subscription/checkout?session_id=${sessionId}`), {
        headers: Authorizor.headers(),
    });
    if (response.ok) {
        const status = await response.json();
        return status;
    }
}

export const syncUserSubscription = async (sessionId: string): Promise<void> => {
    const response = await fetch(apiAddress(`/subscription/sync?session_id=${sessionId}`), {
        headers: Authorizor.headers(),
    });
    if (response.ok) {
        const status = await response.json();
        return status;
    }
}

export const openPortalSession = async (returnPath: string, customerId?: string): Promise<void> => {
    if (!customerId) {
        console.warn("customerId is undefined in openPortalSession");
        return;
    }
    const sessionKey = ulid();
    const returnUrl: string = `${window.location.origin}${returnPath}?key=${sessionKey}`;
    const portalRequest: StripePortalSessionRequest = {
        customerId: customerId,
        returnUrl: returnUrl
    }
    
    const portalResponse = await createPortalSession(portalRequest);
    if (!portalResponse) {
        console.warn("createPortalSession returned null");
        return;
    }
    saveAppState(sessionKey);
    window.location.href = portalResponse.url;
}

export const getPlanButtonText = (currentPlan: SubscriptionLevel, planType: SubscriptionLevel) => {
    if (currentPlan === SubscriptionLevel.NONE) return "Buy";
    const planOrder = [SubscriptionLevel.NONE, SubscriptionLevel.STUDENT, SubscriptionLevel.PRO, SubscriptionLevel.ORGANIZER];
    const currentPlanIndex = planOrder.indexOf(currentPlan);
    const planTypeIndex = planOrder.indexOf(planType);

    if (currentPlanIndex === planTypeIndex) return "Active";
    if (currentPlanIndex < planTypeIndex) return "Upgrade";
    if (currentPlanIndex > planTypeIndex) return "Downgrade";
    return "Buy";
};

export const getPlanDescription = (userSubType: SubscriptionLevel) => {
    switch (userSubType) {
        case SubscriptionLevel.STUDENT:
            return "You are subscribed to the Student plan.";
        case SubscriptionLevel.PRO:
            return "You are subscribed to the Pro plan.";
        case SubscriptionLevel.ORGANIZER:
            return "You are subscribed to the Event Organizer plan.";
        default:
            return "Pick a plan and start your free trial today!";
    }
};

export const formatSubscriptionPrice = (subscriptionLevel: SubscriptionLevel): string | undefined => {
    const catalog = loadPersistedProductCatalog();
    let amount: number | undefined;
    let currency: string | undefined;

    if (catalog ) {
        const price = catalog[subscriptionLevel]?.price;
        if (price) {
            amount = price.amount;
            currency = price.currency;
        }
    }
    if (amount && currency) {
        return formatCurrency(amount, currency);
    }
}

// export const subscribeToPlan = async (level: SubscriptionLevel): Promise<StripeCreateSubscriptionResponse | undefined> => {
//     let checkoutInfo: StripeCreateSubscriptionResponse | undefined;
//     try {
//         let priceId = priceIdFromSubscriptionLevel(level);
//         if (!priceId) {
//             await getProductCatalog();
//             priceId = priceIdFromSubscriptionLevel(level);
//         }
//         if (priceId) {
//         }
//             // POST /subscriptions/checkout
//             const plan: StripeCreateSubscriptionRequest = {
//                 priceId: priceId!
//             }
//             const response = await fetch(apiAddress("/subscriptions/checkout"), {
//                 method: HttpMethod.POST,
//                 headers: Authorizor.headers(),
//                 body: JSON.stringify(plan)
//             })
//             checkoutInfo = await response.json();
//     }
//     catch (err) {
//         console.error("Price ID didn't load.")
//     }
//     return checkoutInfo;
// }

export const getBillingPortalUrl = async (): Promise<UrlResponse | undefined> => {
    let urlResponse: UrlResponse | undefined;
    try {
        const profile = Authorizor.profile;
        if (!profile) {
            throw new Error("Not authorized");
        }
        // GET /subscriptions/billing 
        const response = await fetch(apiAddress("/subscriptions/billing"), {
            headers: Authorizor.headers(),
        })
        urlResponse = await response.json();
    } catch (err) {
        console.error("Error creating billing portal session:", err);
        UserNotificationManager.reportError("Failed to load billing portal. Please try again later.", (err as Error).message);
    }
    return urlResponse;
};


/* module-private functions */

// const priceIdFromSubscriptionLevel = (subscriptionLevel: SubscriptionLevel): string | undefined => {
//     const catalog = loadPersistedProductCatalog();
//     if (catalog && catalog[subscriptionLevel]) {
//         return catalog[subscriptionLevel].price.id;
//     }
// }

const createPortalSession = async (portalRequest: StripePortalSessionRequest): Promise<StripePortalSessionResponse | undefined> => {
    const url = apiAddress("/subscription/portal");
    const options = {
        method: "POST",
        headers: Authorizor.headers(),
        body: JSON.stringify(portalRequest)
    }
    const response = await fetch(url, options);
    if (response.ok) {
        return await response.json();
    }
}

const persistProductCatalog = (catalog: SubscriptionProductRecord) => {
    sessionStorage.setItem(catalogSessionKey, JSON.stringify(catalog));
}

const loadPersistedProductCatalog = (): SubscriptionProductRecord | undefined => {
    const catalogJson = sessionStorage.getItem(catalogSessionKey);
    if (catalogJson) {
        return JSON.parse(catalogJson);
    }
}
